// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CommonMapOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import { get, getOr, each, has, escape, isNil, find } from 'lodash/fp';
import { formatDataByStringFormat } from 'share/components/charting/highcharts/Izenda.HighchartUtils.js';
import { getFieldValue, getConfigDataByRange } from 'share/utils/Izenda.ReportPartUtils';

/**
 * Get point color
 * @param {*} series
 * @param {*} point
 * @param {*} seriesConfig
 * @param {*} fieldConfig
 * @param {*} isCellColor
 */
function getPointColor(series, point, seriesConfig, fieldConfig, isCellColor) {
  const propertyName = `style.${isCellColor ? 'cellColor' : 'color'}`;
  let color;

  if (series.isUserDefinedPoint) {
    const latConfig = get(get('fieldNameAlias', series.latField), seriesConfig);
    const lonConfig = get(get('fieldNameAlias', series.lonField), seriesConfig);

    const latStyle = ChartDataParser.getValueStyle(point.lat, latConfig, {
      element: series.latField,
      record: point.record
    });
    const lonStyle = ChartDataParser.getValueStyle(point.lon, lonConfig, {
      element: series.lonField,
      record: point.record
    });

    color = get(propertyName, latStyle) || get(propertyName, lonStyle);
    return color;
  }

  return get(
    propertyName,
    ChartDataParser.getValueStyle(point.pointName, fieldConfig, {
      element: point.valueField,
      record: point.record
    })
  );
}

/**
 * Apply Point Options Color
 * @param {Array} seriesSet
 * @param {Object} activePointOption
 * @param {Object} seriesConfig
 */
function applyPointOptionsColor(seriesSet: any, activePointOption: any, seriesConfig: Object) {
  const fieldConfig = getOr({}, get('field.name', activePointOption), seriesConfig);

  each(series => {
    if (series.isBackgroundMap || series.hasColor) {
      return true; // continue
    }

    each(item => {
      const cellColor = getPointColor(series, item, seriesConfig, fieldConfig, true);
      if (cellColor) {
        item.color = cellColor;
        item.pointOptionColor = true;
      }

      const textColor = getPointColor(series, item, seriesConfig, fieldConfig, false);
      textColor && (item.textColor = textColor);
    }, series.data);
  }, seriesSet);
}

/**
 * Apply Values Color
 * @param {*} seriesSet
 * @param {*} activePointOption
 * @param {*} seriesConfig
 */
function applyValuesColor(seriesSet: any, activePointOption: any, seriesConfig: Object) {
  const pointType = get('pointType', activePointOption);
  each(series => {
    const seriesKey = get('field.fieldNameAlias', series);
    const fieldConfig = get(['cellColors', pointType].join('.'), seriesConfig[seriesKey]);
    each(point => {
      const cellColor = getConfigDataByRange(point.originalValue || point.value, fieldConfig, {
        element: point.valueField,
        record: point.record
      });

      if (cellColor) {
        point.color = cellColor;
        point.pointOptionColor = false;
      }
    }, series.data);
  }, seriesSet);
}

/**
 * Get tooltip content
 * @param {Object} point
 * @param {Object} pointOption
 * @param {Object} seriesConfig
 */
function getTooltipContent(point: Object, pointOption: Object, seriesConfig: Object, series: Object, color: string) {
  const { valueField } = point;
  const fieldValue = getFieldValue(valueField.columnName, point.record);
  const fieldFormat = get('fieldElement.properties.dataFormattings.format', seriesConfig[valueField.fieldNameAlias]);
  let formatedValue;

  // Check if exist jsFormatString to be prioritized in formating value
  if (!isNil(fieldFormat.jsFormatString) && fieldFormat.jsFormatString !== '') {
    formatedValue = formatDataByStringFormat(
      fieldFormat.formatId,
      fieldValue.value,
      seriesConfig[valueField.fieldNameAlias].fieldDataType
    );
  }

  const valueStyle =
    ChartDataParser.getValueStyle(
      { originalValue: fieldValue.originalValue, value: !isNil(formatedValue) ? formatedValue : fieldValue.value },
      seriesConfig[valueField.fieldNameAlias],
      {
        element: point.valueField,
        record: point.record
      }
    ) || {};

  const pointNameString = getFormattedPointName(point, pointOption, seriesConfig, series);

  return `<span style="color:${point.color || color}">●</span>\
    <span> ${escape(point.valueField.fieldNameAlias)}</span><br>\
    <span>${pointNameString}: ${escape(valueStyle.fomattedValue)}</span>`;
}

/**
 * Get formatted point name
 * @param {Object} point
 * @param {Object} pointOption
 * @param {Object} seriesConfig
 */
function getFormattedPointName(point: Object, pointOption: Object, seriesConfig: Object, series: Object) {
  const { pointName } = point;

  const pointOptionStyle =
    (pointName &&
      ChartDataParser.getValueStyle(pointName, seriesConfig[pointOption.label], {
        element: point.nameField,
        record: point.record
      })) ||
    {};

  return has('fomattedValue', pointOptionStyle)
    ? escape(pointOptionStyle.fomattedValue)
    : buildPointNameByLongLat(point, seriesConfig, series);
}

/**
 * Build Point Name By Long Lat
 * @param {Object} point
 */
function buildPointNameByLongLat(point: Object, seriesConfig, series: Object) {
  if (!has('lat', point) || !has('lon', point)) {
    return '';
  }

  const latConfig = get(get('fieldNameAlias', series.latField), seriesConfig);
  const lonConfig = get(get('fieldNameAlias', series.lonField), seriesConfig);

  const lat = ChartDataParser.getValueStyle(point.lat, latConfig, {
    element: series.latField,
    record: point.record
  });
  const lon = ChartDataParser.getValueStyle(point.lon, lonConfig, {
    element: series.lonField,
    record: point.record
  });

  return `(${get('fomattedValue', lat)}, ${get('fomattedValue', lon)})`;
}

/**
 * Get Name Of Cell Config
 * @param {string} color
 * @param {Object} cellConfig
 */
function getNameOfCellConfig(color: string, cellConfig: any) {
  const findName = config => find({ text: color }, config);
  const found = findName(cellConfig.rangeValue) || findName(cellConfig.rangePercent);
  return get('name', found);
}

export default {
  applyPointOptionsColor,
  applyValuesColor,
  getTooltipContent,
  getFormattedPointName,
  getNameOfCellConfig
};



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.CommonMapOptionsBuilder.js